import { useCallback, useEffect, useState } from "react"
import { css } from "@emotion/css"
import { ColumnInstance } from "react-table"
import { DragDropContext, Droppable, Draggable } from "react-beautiful-dnd"
import DragHandleOutlinedIcon from "@material-ui/icons/DragHandleOutlined"
import Checkbox from "@material-ui/core/Checkbox"
import { useI18n } from "providers/I18n"

export type ListItem<T extends object = {}> = ColumnInstance<T> & {
  content: string
  fixed: boolean
  order: number
}

const grid = 5

const getItemStyle = (
  isVisible: boolean,
  isDragging: boolean,
  draggableStyle: any,
) => ({
  // do not show hidden items
  maxHeight: isVisible ? undefined : 0,

  // some basic styles to make the items look a bit nicer
  padding: grid * 2,
  margin: `0 0 ${grid}px 0`,

  // change background colour if dragging
  background: isDragging ? "#e7e6e6" : "transparent",

  // styles we need to apply on draggables
  ...draggableStyle,
})

const getListStyle = (isDraggingOver: any) => ({
  padding: `${grid} 0`,
  width: 240,
})

const itemStyles = {
  root: css`
    display: flex;
    flex-direction: row;
    justify-content: space-between;
    align-items: center;
    height: var(--spacing-xxl-3);
    user-select: none;
    border-radius: 2px;
    border: 1px solid var(--light-grey);
    padding-left: var(--spacing-xxs) !important;
    margin-bottom: var(--spacing-xxs) !important;
  `,
  row: css`
    display: flex;
    flex-direction: row;
    align-items: center;
  `,
  checkbox: css`
    margin: 0 -7px 0 -7px;
    &.MuiCheckbox-colorPrimary.Mui-disabled {
      color: var(--disabled-grey);
    }
  `,
  dragIcon: css`
    color: var(--dark-grey);
  `,
}

type DragDropListProps<T extends object = {}> = {
  data: Array<ListItem<T>>
  onChange: (newItems: Array<ListItem<T>>) => void
  hasHiddenCols?: boolean
}

function DragDropList<T extends object = {}>({
  data,
  onChange,
  hasHiddenCols,
}: DragDropListProps<T>) {
  const [items, setItems] = useState<Array<ListItem<T>>>([])
  const { t } = useI18n()

  useEffect(() => {
    setItems(data)
  }, [data])

  const reorder = useCallback(
    (list: Array<ListItem<T>>, startIndex: any, endIndex: any) => {
      const result = Array.from(list)
      const [removed] = result.splice(startIndex, 1)
      result.splice(endIndex, 0, removed)

      return result
    },
    [],
  )

  const onDragEnd = (result: any) => {
    // dropped outside the list
    if (!result.destination) {
      return
    }

    // dropped on top or bottom of the list
    if (
      result.destination.index < 1 ||
      result.destination.index >= items.length - 1
    ) {
      return
    }

    const newItems = reorder(
      items,
      result.source.index,
      result.destination.index,
    )

    setItems(newItems)
    onChange(newItems)
  }

  return (
    <DragDropContext onDragEnd={onDragEnd}>
      <Droppable droppableId="droppable">
        {(provided, snapshot) => (
          <div
            {...provided.droppableProps}
            ref={provided.innerRef}
            style={getListStyle(snapshot.isDraggingOver)}
          >
            {items.map((item, index) =>
              item.isVisible ? (
                <Draggable
                  index={index}
                  key={item.id}
                  draggableId={item.id}
                  isDragDisabled={item.fixed || !item.isVisible}
                >
                  {(provided, snapshot) => (
                    <div
                      ref={provided.innerRef}
                      {...provided.draggableProps}
                      {...provided.dragHandleProps}
                      className={itemStyles.root}
                      style={getItemStyle(
                        item.isVisible,
                        snapshot.isDragging,
                        provided.draggableProps.style,
                      )}
                    >
                      <>
                        <div className={itemStyles.row}>
                          <Checkbox
                            {...item.getToggleHiddenProps()}
                            disabled={item.fixed || !hasHiddenCols}
                            color="primary"
                            className={itemStyles.checkbox}
                          />
                          <span className="BodyBody-2BlackRegular">
                            {t(item.content)}
                          </span>
                        </div>
                        {item.fixed ? null : (
                          <DragHandleOutlinedIcon
                            fontSize="default"
                            className={itemStyles.dragIcon}
                          />
                        )}
                      </>
                    </div>
                  )}
                </Draggable>
              ) : null,
            )}
            {provided.placeholder}
          </div>
        )}
      </Droppable>
    </DragDropContext>
  )
}

export { DragDropList }
